import React, { useContext, useEffect, useState } from 'react';
import { Button, HyperLink, getHelpCenterLinks } from 'venn-ui-kit';
import { FlexRow, FlexWrapper, Info, Name, Section, StyledIcon, SmallName, WrapperForSections } from '../shared';
import { CapitalLossDeductionCapInput, TitledNumberInput, UserContext } from 'venn-components';
import type { TaxRateSettings } from 'venn-api';
import { useWorkspaceTaxSettings, useUpdateTaxSettingsMutation } from 'venn-utils';

const DEFAULT_LOSS_CAP = 3000;

const WorkspaceTaxRateDefaultsSection = () => {
  const { hasPermission } = useContext(UserContext);
  const isManageTaxConfigurationPermitted = hasPermission('MANAGE_TAX_CONFIGURATION');
  const isConfigDisabled = !isManageTaxConfigurationPermitted;

  const { mutate: saveTaxSettings, isLoading: isSaving } = useUpdateTaxSettingsMutation();
  const taxSettings = useWorkspaceTaxSettings();
  const results = taxSettings.results;

  const [fedLtRate, setFedLtRate] = useState<number>(results?.fedLtRate ?? 0);
  const [fedStRate, setFedStRate] = useState<number>(results?.fedStRate ?? 0);
  const [niitRate, setNiitRate] = useState<number>(results?.niitRate ?? 0);
  const [stateRate, setStateRate] = useState<number>(results?.stateRate ?? 0);
  const [lossCap, setLossCap] = useState<number>(Math.min(100000, Math.max(0, results?.lossCap ?? DEFAULT_LOSS_CAP)));

  useEffect(() => {
    if (results) {
      setFedLtRate(results.fedLtRate);
      setFedStRate(results.fedStRate);
      setNiitRate(results.niitRate);
      setStateRate(results.stateRate);
      setLossCap(Math.min(100000, Math.max(0, results.lossCap ?? DEFAULT_LOSS_CAP)));
    }
  }, [results]);

  const hasChanged =
    fedLtRate !== (results?.fedLtRate ?? 0) ||
    fedStRate !== (results?.fedStRate ?? 0) ||
    niitRate !== (results?.niitRate ?? 0) ||
    stateRate !== (results?.stateRate ?? 0) ||
    lossCap !== (results?.lossCap ?? DEFAULT_LOSS_CAP);

  const onSubmit = () => {
    saveTaxSettings({
      fedLtRate,
      fedStRate,
      lossCap,
      niitRate,
      stateRate,
    } satisfies TaxRateSettings);
  };

  return (
    <WrapperForSections>
      <Section>
        <FlexRow>
          <FlexWrapper>
            <StyledIcon type="badge-percent" />
            <Name>Tax Rate Defaults</Name>
          </FlexWrapper>
          <div className="flex items-center">
            <Button disabled={!hasChanged || isSaving || isConfigDisabled} icon="save" type="submit" onClick={onSubmit}>
              Save changes
            </Button>
          </div>
        </FlexRow>
        {getHelpCenterLinks().TAX_RATE_HELP_HREF && (
          <Info>
            <HyperLink
              href={getHelpCenterLinks().TAX_RATE_HELP_HREF}
              target="_blank"
              rel="noopener noreferrer"
              className="qa-knowledge-base-link font-bold"
            >
              Learn More
            </HyperLink>
          </Info>
        )}
      </Section>
      <Section>
        <FlexRow>
          <div>
            <SmallName>Federal Capital Gains Tax Rates</SmallName>
            <div className="text-[10px] mt-1">
              The default Federal Tax Rates applicable to Long-Term and Short-Term capital gains.
            </div>
          </div>
          <div className="grid grid-cols-2 gap-10">
            <TitledNumberInput
              title="Long Term"
              value={fedLtRate}
              disabled={isConfigDisabled}
              setValue={setFedLtRate}
            />
            <TitledNumberInput
              title="Short Term"
              value={fedStRate}
              disabled={isConfigDisabled}
              setValue={setFedStRate}
            />
          </div>
        </FlexRow>
      </Section>
      <Section>
        <FlexRow>
          <div>
            <SmallName>Net Investment Income Tax Rate (NIIT)</SmallName>
            <div className="text-[10px] mt-1">
              The default NIIT rate if Modified Adjusted Gross Income (MAGI) is above an applicable threshold.
            </div>
          </div>
          <TitledNumberInput
            title="NIIT"
            value={niitRate}
            disabled={isConfigDisabled}
            setValue={setNiitRate}
            skipTitle
          />
        </FlexRow>
      </Section>
      <Section>
        <FlexRow>
          <div>
            <SmallName>State Tax Rate</SmallName>
            <div className="text-[10px] mt-1">
              The default tax rate applicable to home state (e.g., when calculating state specific capital gains tax).
            </div>
          </div>
          <TitledNumberInput
            title="State Tax"
            value={stateRate}
            disabled={isConfigDisabled}
            setValue={setStateRate}
            skipTitle
          />
        </FlexRow>
      </Section>
      <Section>
        <FlexRow>
          <div>
            <SmallName>Capital Loss Deduction Cap</SmallName>
            <div className="text-[10px] mt-1">
              The default maximum annual capital loss deduction against ordinary income.
            </div>
          </div>
          <CapitalLossDeductionCapInput value={lossCap} onChange={setLossCap} disabled={isConfigDisabled} />
        </FlexRow>
      </Section>
    </WrapperForSections>
  );
};

export default WorkspaceTaxRateDefaultsSection;
